import { Box, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Compact, semantic status indicator. Inputs map to a small fixed set of
 * tones so colour is never decided ad-hoc at the call site (avoids the v1
 * problem where every list view re-invented "green = good").
 *
 * Tones:
 *   - success / warning / danger / info — the four MUI status colors
 *   - neutral                            — for SENT/PENDING-style metadata
 *   - mono                               — sentinel for "not applicable" (no policy, etc.)
 */

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'mono';

const TONE_KEYS: Record<Tone, { fg: 'success' | 'warning' | 'danger' | 'info'; bg: 'successBg' | 'warningBg' | 'dangerBg' | 'infoBg' } | null> = {
  success: { fg: 'success', bg: 'successBg' },
  warning: { fg: 'warning', bg: 'warningBg' },
  danger:  { fg: 'danger',  bg: 'dangerBg'  },
  info:    { fg: 'info',    bg: 'infoBg'    },
  neutral: null,
  mono: null,
};

export function StatusPill({
  tone = 'neutral',
  children,
  dot = true,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
}) {
  const theme = useTheme();
  const keys = TONE_KEYS[tone];
  const fg = keys ? theme.palette.status[keys.fg] : theme.palette.text.secondary;
  const bg = keys ? theme.palette.status[keys.bg] : theme.palette.surface.sunken;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: theme.radius.pill + 'px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.2,
        color: fg,
        bgcolor: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: fg,
            flexShrink: 0,
          }}
        />
      )}
      {children}
    </Box>
  );
}

/** Map a webhook outbox status string to a tone. Reused by both Platform + Tenant webhook pages. */
export function webhookStatusTone(status: string | undefined | null): Tone {
  switch (status) {
    case 'SENT': return 'success';
    case 'PENDING': return 'info';
    case 'IN_FLIGHT': return 'warning';
    case 'DLQ': return 'danger';
    default: return 'neutral';
  }
}

/** Map an audit event status to a tone. */
export function auditStatusTone(status: string | undefined | null): Tone {
  switch (status) {
    case 'PASS':
    case 'SUCCESS': return 'success';
    case 'FAIL':
    case 'FAILURE': return 'danger';
    case 'PARTIAL': return 'warning';
    case 'TIMEOUT': return 'warning';
    default: return 'neutral';
  }
}
